import { Router, UrlTree } from '@angular/router';

/**
 * #1160 — navigation vers un `returnUrl` en y AJOUTANT des query params.
 *
 * Le piège corrigé : `router.navigate([returnUrl], { queryParams })` traite
 * `returnUrl` comme UN SEUL segment de chemin. Tant que le `returnUrl` est nu
 * (`/new-shipment`) le résultat est correct par accident ; dès qu'il porte ses
 * propres paramètres — et c'est déjà le cas de tous ceux produits par
 * `TeamOnlyGuard` / `AuthenticatedGuard`, qui posent `state.url` entier, ou par
 * `parcel-details` (`/details?trackingID=…`) — le `?` est ENCODÉ dans le
 * segment (`/details%3FtrackingID=…`) et la navigation tombe sur le wildcard
 * 404. Aujourd'hui les deux ne se croisent pas (`restore` n'est posé que par le
 * tunnel d'envoi, sur des `returnUrl` nus) ; la bascule MyBefret (#1140)
 * multiplie les URLs paramétrées et fera se croiser les deux.
 *
 * `parseUrl` fait le travail correctement : il rend un `UrlTree` où chemin,
 * query params et fragment sont déjà séparés. On fusionne les paramètres
 * supplémentaires par-dessus et on navigue avec l'arbre.
 *
 * Équivalence stricte avec l'ancien code pour les cas EXISTANTS (money path) :
 * un `returnUrl` sans query param produit exactement la même URL finale.
 * Vérifié par `return-url.util.spec.ts`.
 */
export function returnUrlTreeWith(
  router: Router,
  returnUrl: string,
  extraQueryParams: Record<string, string | number | boolean>
): UrlTree {
  const tree = router.parseUrl(returnUrl);
  tree.queryParams = { ...tree.queryParams, ...extraQueryParams };
  return tree;
}

/**
 * #1315 — un `returnUrl` reçu en query param, ramené à un chemin INTERNE.
 *
 * Deux pièges, tous les deux déjà payés ailleurs dans le dépôt :
 *
 * 1. **Redirection ouverte.** Le `returnUrl` arrive d'une query string, donc de
 *    l'extérieur. `//evil.tld` est un chemin *protocol-relative* : passé tel
 *    quel à `navigateByUrl`, le navigateur quitte le domaine. On n'accepte
 *    donc qu'un chemin qui commence par UN seul `/`.
 * 2. **`navigateByUrl`, jamais `navigate([url])`.** Passé en commande, un
 *    `returnUrl` porteur d'une query string voit son `?` encodé en `%3F` et
 *    tombe sur le wildcard 404 (cf. `returnUrlTreeWith` ci-dessus). Cette
 *    fonction rend une CHAÎNE, à donner à `navigateByUrl` — c'est volontaire.
 *
 * La logique vient de `team-login.component.ts`, qui l'avait résolue en
 * premier ; elle est remontée ici pour que les écrans 2FA la partagent au lieu
 * d'en réécrire chacun une variante.
 *
 * #1976 — deux formes passaient encore le seul test du `/` initial :
 * - `/\evil.tld` : le navigateur lit `\` comme `/`, soit `//evil.tld` ;
 * - un caractère de contrôle (tabulation, saut de ligne), que l'analyseur d'URL
 *   du navigateur supprime avant d'interpréter le reste.
 * Aucun `returnUrl` légitime ne contient l'un ou l'autre : ils sont refusés
 * partout dans la chaîne, pas seulement en tête. Tous les écrans qui lisent un
 * `returnUrl` passent par ici (connexion client, téléphone, profil, mot de
 * passe, récupération de compte, `team-login`, écrans 2FA).
 *
 * @param returnUrl valeur brute du query param (souvent `undefined`)
 * @param fallback destination quand il est absent ou non interne
 */
export function safeInternalReturnUrl(
  returnUrl: string | null | undefined,
  fallback: string,
): string {
  return typeof returnUrl === 'string'
    && /^\/(?!\/)/.test(returnUrl)
    // eslint-disable-next-line no-control-regex
    && !/[\\\u0000-\u001F\u007F]/.test(returnUrl)
    ? returnUrl
    : fallback;
}
